import { useCallback, useEffect, useRef, useState } from "react";
import { TourFlight, type ScreenBox, type ScreenPoint } from "@omnabase/ui";
import { legalBehind, type Home, type TicketRow } from "@omnabase/shared";
import { api } from "../lib/api";
import { showSetup } from "../lib/setup";
import { useCan } from "../lib/access";
import { listUrl, useMediaQuery } from "../lib/inbox";
import { useNavigate, useWs } from "../lib/nav";
import { TOUR_MIN_WIDTH, runTour, tourRunning, tourStartsByItself, tourStops, tourTicket, type TourEnd } from "../lib/tour";
import { useSession } from "./store";
import { useToasts } from "./toast";

/** Help's "Take the tour" reaches the workspace's runner through this (one runner per open workspace) */
let starter: ((returnFocus: Element | null) => void) | null = null;
export const takeTheTour = (returnFocus: Element | null) => starter?.(returnFocus);
/** the tour starts by itself at most once per page load (Done or Skip writes the mark; a second workspace opened meanwhile must not restart it) */
let startedByItself = false;

/** what the goodbye tip says (the founder, 2026-10-07) */
export const TOUR_TIP_TEXT = "Take the tour again anytime under Get help.";

/** M11 round three: the tour's place in the workspace (mounted once beside its routes, so it outlives each screen's shell). It starts by itself
 *  on a person's first open at 768 px or more while `Me.tourSeen` is false, once no "You joined" toast, legal notice or dialog is up; Help's
 *  "Take the tour" starts it again at any time. Every end, at any stop and after a restart too, sends the kit's dot to the account button
 *  (the founder, 2026-10-07, Omnalingo's flight); the tip under it only on the first completion (the person had not seen the tour when this
 *  run started, and no run ended yet in this page). */
export function Tour() {
  const { me, workspace } = useSession();
  const { ws, slug } = useWs();
  const navigate = useNavigate();
  const can = useCan();
  const wide = useMediaQuery(`(min-width: ${TOUR_MIN_WIDTH}px)`);
  const toasts = useToasts();
  const joinedToast = toasts.some((t) => t.text.startsWith("You joined"));
  const legalNotice = workspace.me.role === "owner" && (legalBehind(workspace.terms.version, workspace.terms.current) || legalBehind(workspace.dpa.version, workspace.dpa.current));
  const latest = useRef({ can, navigate, ws, slug, seen: me.tourSeen }); latest.current = { can, navigate, ws, slug, seen: me.tourSeen };
  const [flight, setFlight] = useState<Flight | null>(null);
  const endedOnce = useRef(false); // the session's `tourSeen` is not read again after the first end, so the page remembers it

  const start = useCallback(async (returnFocus: Element | null) => {
    if (tourRunning()) return;
    setFlight(null);
    const { can, navigate, ws, slug, seen } = latest.current;
    const first = seen === false && !endedOnce.current;
    const { ticket, checklist } = await tourFacts(slug);
    const stops = tourStops(can, window.innerWidth, ticket !== null, checklist);
    await runTour({ stops, ticket, navigate: (to) => navigate(to), returnFocus,
      onEnd: (end) => { endedOnce.current = true; setFlight(flightOf(end, first)); },
      places: { home: ws(""), inbox: ws("/inbox/queue"), ticket: (n) => ws(`/inbox/queue/${n}`), chat: ws("/chat/general") } });
  }, []);
  const landed = useCallback(() => setFlight(null), []);

  useEffect(() => {
    starter = (returnFocus) => { void afterDialogs().then(() => start(returnFocus ?? document.activeElement)); };
    return () => { starter = null; };
  }, [start]);

  const dialogUp = typeof document !== "undefined" && !!document.querySelector(".ob-dialog");
  useEffect(() => {
    if (startedByItself || !tourStartsByItself({ width: wide ? window.innerWidth : 0, seen: me.tourSeen, joinedToast, legalNotice, dialogOpen: dialogUp })) return;
    startedByItself = true;
    void start(document.activeElement);
  }, [wide, me.tourSeen, joinedToast, legalNotice, dialogUp, start]);
  return flight ? <TourFlight from={flight.from} to={flight.to} target={flight.target} tip={flight.first ? TOUR_TIP_TEXT : undefined} onDone={landed} /> : null;
}

/** What the stops depend on, read when the tour starts: the Home read names the sample ticket and the setup checklist for the owner, whose
 *  they are; anyone else's single-ticket stop opens the first of their queue (none when it is empty), and the next-steps stop reads Home's
 *  notices. A failed read only leaves a stop out. */
async function tourFacts(slug: string): Promise<{ ticket: number | null; checklist: boolean }> {
  const home = await api.get<Home>(`/api/v1/w/${slug}/home?range=7d`).catch(() => null);
  const sample = home?.setup?.sample ?? null;
  const queue = sample === null ? await api.get<{ rows: TicketRow[] }>(listUrl(slug, { view: "queue" })).then((p) => p.rows, () => null) : null;
  return { ticket: tourTicket(sample, queue), checklist: showSetup(home?.setup) };
}

type Flight = { from: ScreenPoint; to: ScreenBox; target: HTMLElement; first: boolean };
/** the goodbye: where the popover was (else the middle of the window), the account button where Get help lives (Omnalingo's "menu"), and
 *  whether this was the first completion; none when the button is not on screen */
function flightOf(end: TourEnd, first: boolean): Flight | null {
  const button = document.querySelector<HTMLElement>('[data-tour="help"]');
  const to = button?.getBoundingClientRect();
  if (!button || !to || to.width === 0 || to.bottom < 0 || to.top > window.innerHeight) return null;
  return { from: end.from ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 }, to: { top: to.top, right: to.right, bottom: to.bottom, left: to.left }, target: button, first };
}

/** Help closes before the tour starts: wait until no dialog is left, then a frame, so the popover takes the focus after the page settled */
async function afterDialogs() {
  for (let i = 0; i < 100 && document.querySelector(".ob-dialog"); i++) await new Promise((r) => setTimeout(r, 30));
  await new Promise((r) => requestAnimationFrame(() => r(null)));
}
